// vendors
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
// libs
import { NotebookTypes } from '@mode/notebooks/contract';
// feature
import { LibraryGroupKey, NotebookLibrary } from './notebook-supported-libraries.types';

@Injectable()
export class NotebookSupportedLibrariesService {
  constructor() {}

  /**
   * Return all supported libraries
   * @param library
   * @returns
   */
  getLibraries(library: NotebookTypes.NotebookType): Observable<Array<NotebookLibrary>> {
    const libraries = LIBRARIES.filter((l) => l.environments.indexOf(library) !== -1);
    return of(libraries);
  }

  /**
   * Find libraries that have the searchTerm in either the label or description
   * @param searchTerm
   * @param libraries
   * @returns
   */
  search(searchTerm: string, libraries: Array<NotebookLibrary>): Observable<Array<NotebookLibrary>> {
    return of(
      libraries.filter(
        (l) =>
          l.label.toLocaleLowerCase().indexOf(searchTerm.toLocaleLowerCase()) !== -1 ||
          l.description.toLocaleLowerCase().indexOf(searchTerm.toLocaleLowerCase()) !== -1
      )
    );
  }
}

// In furture we may want to return this as part of an api service
export const LIBRARIES: Array<NotebookLibrary> = [
  // Frequently Used: R
  {
    label: 'dplyr',
    description: 'grammar of data manipulation',
    href: 'http://dplyr.tidyverse.org/#usage',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.frequent],
    keywords: [],
  },
  {
    label: 'ggplot2',
    description: 'system for creating graphics',
    href: 'http://ggplot2.tidyverse.org/index.html#usage',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.frequent],
    keywords: [],
  },
  {
    label: 'httr',
    description: 'tools for working with URLs &amp; HTTP',
    href: 'https://www.rdocumentation.org/packages/httr/versions/1.4.4',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.frequent, LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'kernlab',
    description: 'kernel-based machine learning lab',
    href: 'https://cran.r-project.org/web/packages/kernlab/kernlab.pdf',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.frequent, LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'lubridate',
    description: 'do more with dates and times',
    href: 'https://www.rdocumentation.org/packages/lubridate/versions/1.8.0',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.frequent],
    keywords: [],
  },
  {
    label: 'MASS',
    description: 'functions &amp; datasets to support Venables &amp; Ripley',
    href: 'https://www.rdocumentation.org/packages/MASS/versions/7.3-58.1',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.frequent, LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'modelr',
    description: 'modelling functions that work with the pipe',
    href: 'https://github.com/tidyverse/modelr#modelr',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.frequent, LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'RColorBrewer',
    description: 'ColorBrewer palettes',
    href: 'https://www.rdocumentation.org/packages/RColorBrewer/versions/1.1-3/topics/RColorBrewer',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.frequent, LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'scales',
    description: 'scale functions for visualizations',
    href: 'https://www.rdocumentation.org/packages/scales/versions/1.2.1',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.frequent, LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'stringr',
    description: 'fast, correct implementations of string manipulations',
    href: 'https://github.com/tidyverse/stringr#overview',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.frequent],
    keywords: [],
  },
  {
    label: 'tidyr',
    description: 'easily create tidy data',
    href: 'http://tidyr.tidyverse.org/',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.frequent, LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'plotly',
    description: 'create interactive graphics',
    href: 'https://www.rdocumentation.org/packages/plotly/versions/4.10.0',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.frequent, LibraryGroupKey.viz],
    keywords: [],
  },

  // Frequently Used: Python

  {
    label: 'matplotlib',
    description: '2D plotting library',
    href: 'http://matplotlib.org/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.frequent, LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'numpy',
    description: 'scientific computing library',
    href: 'http://www.numpy.org/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.frequent, LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'pandas',
    description: 'data structures &amp; data analysis tools',
    href: 'http://pandas.pydata.org/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.frequent, LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'prophet',
    description: 'procedure for forecasting time series data',
    href: 'https://facebook.github.io/prophet/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.frequent, LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'plotly',
    description: 'create interactive graphics',
    href: 'https://plot.ly/python/getting-started/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.frequent, LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'seaborn',
    description: 'viz library to draw statistical graphics',
    href: 'https://stanford.edu/~mwaskom/software/seaborn/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.frequent, LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'scipy',
    description: 'mathematics, science &amp; engineering',
    href: 'https://www.scipy.org/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.frequent, LibraryGroupKey.stats],
    keywords: [],
  },

  // Data Manipulation & Wrangling: R

  {
    label: 'data.table',
    description: 'extends data.frame',
    href: 'https://cran.r-project.org/web/packages/data.table/vignettes/datatable-intro.html',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'dplyr',
    description: 'a grammar of data manipulation',
    href: 'http://dplyr.tidyverse.org/#usage',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'forcats',
    description: 'working with categorical variables',
    href: 'https://www.rdocumentation.org/packages/forcats/versions/0.5.2',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'glue',
    description: 'glue strings to data',
    href: 'https://github.com/tidyverse/glue#usage',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'iterators',
    description: 'provides iterator construct for R',
    href: 'https://www.rdocumentation.org/packages/iterators/versions/1.0.14',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'itertools',
    description: 'various tools for creating iterators',
    href: 'https://www.rdocumentation.org/packages/itertools/versions/0.1-3',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'janitor',
    description: 'tools for examining and cleaning data',
    href: 'https://cran.r-project.org/web/packages/janitor/index.html',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'lubridate',
    description: 'do more with dates and times',
    href: 'https://www.rdocumentation.org/packages/lubridate/versions/1.8.0',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'magrittr',
    description: 'a forward-pipe operator',
    href: 'https://cran.r-project.org/web/packages/magrittr/vignettes/magrittr.html',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'pillar',
    description: 'tools for styling columns of data',
    href: 'http://pillar.r-lib.org/index.html',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'purrr',
    description: 'functional programming toolkit',
    href: 'https://github.com/tidyverse/purrr#usage',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'reshape2',
    description: 'transform data between wide &amp; long',
    href: 'https://www.rdocumentation.org/packages/reshape2/versions/1.4.4',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'stringr',
    description: 'work with character strings &amp; regex',
    href: 'https://github.com/tidyverse/stringr#overview',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'tidytext',
    description: 'convert text to and from tidy format',
    href: 'https://cran.r-project.org/web/packages/tidytext/index.html',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'utf8',
    description: "fixes bugs in R's UTF-8 handling",
    href: 'https://github.com/patperry/r-utf8#usage',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'xml2',
    description: 'parse xml',
    href: 'https://www.rdocumentation.org/packages/xml2/versions/1.3.3',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },

  // Data Manipulation & Wrangling: Python

  {
    label: 'arrow',
    description: 'date & time manipulation & formatting',
    href: 'https://arrow.readthedocs.io/en/latest/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'beautifulsoup4',
    description: 'Beautiful Soup, for parsing HTML, JSON &amp; XML data',
    href: 'https://www.crummy.com/software/BeautifulSoup/bs4/doc/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'engarde',
    description: 'defensive data analysis',
    href: 'http://engarde.readthedocs.io/en/latest/index.html',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'jsonify',
    description: 'converts .csv files to .json',
    href: 'https://pypi.python.org/pypi/jsonify/0.5',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'numexpr',
    description: 'fast numerical array expression evaluator',
    href: 'https://pypi.python.org/pypi/numexpr',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'pandas_profiling',
    description: 'generates profile reports from a pandas DataFrame',
    href: 'https://pypi.org/project/pandas-profiling/1.4.0/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'pandasql',
    description: 'queries pandas dataframes using SQL syntax',
    href: 'https://pypi.python.org/pypi/pandasql',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'prettytable',
    description: 'easily display tabular data as ASCII table',
    href: 'https://pypi.python.org/pypi/PrettyTable',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'shapely',
    description: 'manipulation & analysis of geometric objects',
    href: 'https://shapely.readthedocs.io/en/latest/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'snowflake-snowpark',
    description: 'Lets you build and run data workflows directly in Snowflake using Python',
    href: 'https://docs.snowflake.com/en/developer-guide/snowpark/python/index',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },
  {
    label: 'tabulate',
    description: 'pretty-print tabular data',
    href: 'https://pypi.python.org/pypi/tabulate',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.wrangling],
    keywords: [],
  },

  // Machine Learning & Stats: R

  {
    label: 'BTYD',
    description: 'implementing buy-til-you-die models',
    href: 'https://www.rdocumentation.org/packages/BTYD/versions/2.4',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'BTYDplus',
    description: 'extends BTYD',
    href: 'https://www.rdocumentation.org/packages/BTYDplus/versions/1.2.0',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'caret',
    description: 'streamlines creation of predictive models',
    href: 'http://topepo.github.io/caret/index.html',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'causalImpact',
    description: 'estimates causal effects on time series',
    href: 'https://google.github.io/CausalImpact/CausalImpact.html',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'cluster',
    description: 'cluster analysis extended Rousseeuw et al.',
    href: 'https://www.rdocumentation.org/packages/cluster/versions/2.1.4',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'forecast',
    description: 'forecasting for time series &amp; linear models',
    href: 'https://www.rdocumentation.org/packages/forecast/versions/8.18',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'hts',
    description: 'hierarchical &amp; grouped time series',
    href: 'https://www.rdocumentation.org/packages/hts/versions/6.0.2',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'lars',
    description: 'least angle regression, lasso &amp; forward stagewise',
    href: 'https://www.rdocumentation.org/packages/lars/versions/1.3',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'nnet',
    description: 'feed-forward neural networks &amp; multinomial log-linear models ',
    href: 'https://www.rdocumentation.org/packages/nnet/versions/7.3-18',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'prophet',
    description: 'automatic forecasting procedure',
    href: 'https://www.rdocumentation.org/packages/prophet/versions/1.0',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'tm',
    description: 'text mining package',
    href: 'https://www.rdocumentation.org/packages/tm/versions/0.7-9',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'zoo',
    description: 'S3 infrastructure for regular &amp; irregular time series',
    href: 'https://www.rdocumentation.org/packages/zoo/versions/1.8-11',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },

  // Machine Learning & Stats: Python
  {
    label: 'cvxopt',
    description: 'convex optimization library',
    href: 'https://cvxopt.org/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'emcee',
    description: 'an MIT <a href="http://msp.org/camcos/2010/5-1/index.xhtml">MCMC</a> library',
    href: 'http://dan.iel.fm/emcee/current/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'hdbscan',
    description: 'Hierarchical Density-Based Spatial Clustering of Applications with Noise',
    href: 'https://pypi.python.org/pypi/hdbscan/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'keras',
    description: 'high-level neural networks API',
    href: 'https://keras.io/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'interpret',
    description: 'fit interpretable ML models and explain blackbox ML',
    href: 'https://pypi.org/project/interpret/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'lifelines',
    description: 'survival analysis in Python',
    href: 'http://lifelines.readthedocs.io/en/latest/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'lifetimes',
    description: 'package for analyzing user behavior',
    href: 'https://pypi.python.org/pypi/Lifetimes',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'scikit-image',
    description: 'image processing library',
    href: 'http://scikit-image.org/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'scikit-learn',
    description: 'tools for data mining &amp; analysis',
    href: 'http://scikit-learn.org/stable/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'scikits-bootstrap',
    description: 'bootstrap confidence interval algorithms for scipy',
    href: 'https://github.com/cgevans/scikits-bootstrap/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'statsmodels',
    description: 'estimate statistical models &amp; perform statistical tests',
    href: 'http://statsmodels.sourceforge.net/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'sympy',
    description: 'symbolic mathematics',
    href: 'http://www.sympy.org/en/index.html',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'tensorflow',
    description: 'numerical computation using data flow graphs',
    href: 'https://www.tensorflow.org/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'tensorflow-decision-forests',
    description: 'train, run and interpret decision forest models in TensorFlow',
    href: 'https://pypi.org/project/tensorflow-decision-forests/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },
  {
    label: 'xgboost',
    description: 'optimized distributed gradient boosting library',
    href: 'https://xgboost.readthedocs.io/en/latest/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.stats],
    keywords: [],
  },

  // Visualization: R

  {
    label: 'colorspace',
    description: 'color space manipulation',
    href: 'https://colorspace.r-forge.r-project.org/reference/index.html',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'DiagrammeR',
    description: 'build graph/network structures',
    href: 'https://cran.r-project.org/web/packages/DiagrammeR/index.html',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'dichromat',
    description: 'color schemes for dichromats',
    href: 'https://www.rdocumentation.org/packages/dichromat/versions/2.0-0.1',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'ggdendro',
    description: 'dendrograms &amp; tree plots with ggplot2',
    href: 'https://www.rdocumentation.org/packages/ggdendro/versions/0.1.23',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'GGally',
    description: 'extension to ggplot2',
    href: 'http://ggobi.github.io/ggally/',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'ggplot2',
    description: 'system for creating graphics',
    href: 'http://ggplot2.tidyverse.org/index.html#usage',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'ggpubr',
    description: 'publication-ready ggplot2 plots',
    href: 'https://www.rdocumentation.org/packages/ggpubr/versions/0.4.0',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'ggridges',
    description: 'ridgeline plots in ggplot2',
    href: 'https://www.rdocumentation.org/packages/ggridges/versions/0.5.4',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'gtable',
    description: 'arrange \'grobs\' in "tables"',
    href: 'https://www.rdocumentation.org/packages/gtable/versions/0.3.1',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'ggthemes',
    description: 'extra themes, scales &amp; geoms for ggplot2',
    href: 'https://jrnold.github.io/ggthemes/reference/index.html',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'labeling',
    description: 'axis labeling',
    href: 'https://www.rdocumentation.org/packages/labeling/versions/0.4.2',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'lattice',
    description: 'trellis graphics for R',
    href: 'http://lattice.r-forge.r-project.org/documentation.php',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'leaflet',
    description: 'create interactive web maps',
    href: 'https://cran.r-project.org/web/packages/leaflet/index.html',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'munsell',
    description: 'utilities for using Munsell colors',
    href: 'https://www.rdocumentation.org/packages/munsell/versions/0.5.0',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'viridisLite',
    description: 'port of matplotlib color maps ',
    href: 'https://www.rdocumentation.org/packages/viridisLite/versions/0.4.1/topics/viridis',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },

  // Visualization: Python

  {
    label: 'folium',
    description: 'build Leaflet.js maps in Python',
    href: 'https://folium.readthedocs.io/en/latest/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'gviz_api',
    description: 'helper library for Google Visualization API',
    href: 'https://developers.google.com/chart/interactive/docs/dev/gviz_api_lib',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'igraph',
    description: 'network analysis tools',
    href: 'http://igraph.org/python/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'mapbox',
    description: 'client for Mapbox web services',
    href: 'https://mapbox-mapbox.readthedocs-hosted.com/en/latest/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'patsy',
    description: 'describe statistical models &amp; build design matrices',
    href: 'https://pypi.python.org/pypi/patsy',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'pygal',
    description: 'create interactive svg charts',
    href: 'http://pygal.org/en/stable/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'pygraphviz',
    description: 'interface for Graphviz graph layout & visualizations',
    href: 'http://pygraphviz.github.io/documentation/pygraphviz-1.5/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'pyproj',
    description: 'cartographic transformations & geodetic computations',
    href: 'https://jswhit.github.io/pyproj/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'squarify',
    description: 'implementation of the squarify treemap layout algorithm',
    href: 'https://github.com/laserson/squarify',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },
  {
    label: 'wordcloud',
    description: 'wordcloud generator in Python',
    href: 'https://github.com/amueller/word_cloud',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.viz],
    keywords: [],
  },

  // Everything Else: R

  {
    label: 'assertthat',
    description: 'easy pre- and post-assertions',
    href: 'https://www.rdocumentation.org/packages/assertthat/versions/0.2.1',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'blob',
    description: 'S3 class to represent BLOBs',
    href: 'https://www.rdocumentation.org/packages/blob/versions/1.2.3/topics/blob',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'digest',
    description: 'create compact hash digests of R objects',
    href: 'https://www.rdocumentation.org/packages/digest/versions/0.6.30/topics/digest',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'fpp3',
    description: "datasets referenced in book 'Forecasting: principles and practice'",
    href: 'https://cran.r-project.org/web/packages/fpp3/index.html',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'lazyeval',
    description: 'lazy (non-standard) evaluation',
    href: 'https://github.com/hadley/lazyeval#lazyeval',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'proto',
    description: 'prototype object-based programming ',
    href: 'https://cran.r-project.org/web/packages/proto/proto.pdf',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'rlang',
    description: 'functions for base types &amp; core R &amp; tidyverse features',
    href: 'https://www.rdocumentation.org/packages/rlang/versions/1.0.6',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'tidyverse',
    description: 'Collection of essential R packages for data science',
    href: 'https://www.rdocumentation.org/packages/tidyverse/versions/1.3.2',
    environments: [NotebookTypes.NotebookType.R],
    group: [LibraryGroupKey.other],
    keywords: [],
  },

  // Everything Else: Python

  {
    label: 'cufflinks',
    description: 'bind Plotly directly to pandas dataframes',
    href: 'https://pypi.python.org/pypi/cufflinks',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'dask',
    description: 'flexible open-source Python library for parallel computing',
    href: 'https://www.dask.org/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'duckdb',
    description: 'in-process database management system focused on analytical query processing',
    href: 'https://duckdb.org/docs/api/python/overview.html',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'fiona',
    description: 'read &amp; write geospatial data files',
    href: 'http://toblerity.org/fiona/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'geopandas',
    description: 'extends pandas to allow spatial operations on geometric types',
    href: 'http://geopandas.org/index.html',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'networkx',
    description: 'create, manipulate &amp; study networks',
    href: 'https://networkx.github.io/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'nltk',
    description: 'natural language toolkit',
    href: 'http://www.nltk.org/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'pympler',
    description: 'measure, monitor and analyze the memory behavior of Python objects',
    href: 'https://pypi.org/project/Pympler/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'pysal',
    description: 'geospatial analysis library',
    href: 'http://pysal.org/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'pyzipcode',
    description: 'query zip codes &amp; location data',
    href: 'https://pypi.python.org/pypi/pyzipcode',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'requests',
    description: 'allows HTTP requests',
    href: 'http://docs.python-requests.org/en/master/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'scrapy',
    description: 'scraping web pages',
    href: 'https://scrapy.org/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'six',
    description: 'Python 2 & 3 compatibility library',
    href: 'https://six.readthedocs.io/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'spacy',
    description: 'advanced natural language processing',
    href: 'https://spacy.io/usage/spacy-101',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'textblob',
    description: 'simple API for common NLP tasks',
    href: 'http://textblob.readthedocs.io/en/dev/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'ua_parser',
    description: 'fast &amp; reliable user agent parser',
    href: 'https://pypi.python.org/pypi/ua-parser',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
  {
    label: 'urllib3',
    description: 'HTTP client for python',
    href: 'https://urllib3.readthedocs.io/en/latest/',
    environments: [NotebookTypes.NotebookType.Python3, NotebookTypes.NotebookType.Python3Edge],
    group: [LibraryGroupKey.other],
    keywords: [],
  },
];
